import { createClient, type SupabaseClient, type PostgrestError } from "@supabase/supabase-js"

import type Search from "../types/search"

export type { SupabaseClient }

// ─── Config ──────────────────────────────────────────────────────────────────

export interface SupabaseConfig {
  domain?: string
  environment?: string
  platform?: string
  supabaseAnonKey?: string
  supabaseUrl?: string
}

export function buildSupabaseUrl(config: SupabaseConfig): string {
  const { domain, environment, platform, supabaseUrl } = config
  if (supabaseUrl) return supabaseUrl

  const dbPrefix = process.env.NEXT_PUBLIC_DB_PREFIX ?? "database"
  const subdomainSuffix = process.env.NEXT_PUBLIC_SUBDOMAIN_SUFFIX ?? ""
  const suffix = subdomainSuffix ? `-${subdomainSuffix}` : ""

  if (platform === "local") return `http://${dbPrefix}.localhost:83`
  if (environment === "production") return `https://${dbPrefix}${suffix}.${domain}`
  return `https://${dbPrefix}-${environment}${suffix}.${domain}`
}

// ─── Client ──────────────────────────────────────────────────────────────────

let _client: SupabaseClient | null = null

export function initSupabaseClient(url: string, anonKey: string): SupabaseClient {
  _client = createClient(url, anonKey, {
    realtime: {
      // Default 10s is too tight when Realtime tenant CDC is still warming up.
      timeout: 30_000,
    },
  })
  return _client
}

export function getClient(): SupabaseClient {
  return _client!
}

// ─── Auth helpers ─────────────────────────────────────────────────────────────

export async function getAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await getClient().auth.getSession()
  return session?.access_token ?? null
}

export async function waitForRealtimeReady(timeoutMs = 15_000): Promise<void> {
  const client = getClient()
  client.realtime.connect()

  const started = Date.now()
  while (!client.realtime.isConnected()) {
    if (Date.now() - started > timeoutMs) {
      throw new Error("Realtime WebSocket did not connect in time")
    }
    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  if (process.env.NODE_ENV === "development") {
    console.log("[supabase/realtime] WebSocket connected")
  }
}

export async function syncRealtimeAuth(): Promise<string | null> {
  const client = getClient()
  const { data: sessionData } = await client.auth.getSession()
  let accessToken = sessionData.session?.access_token

  if (!accessToken) {
    const { data: refreshed } = await client.auth.refreshSession()
    accessToken = refreshed.session?.access_token
  }

  if (accessToken) {
    await client.realtime.setAuth(accessToken)
    await waitForRealtimeReady()
    if (process.env.NODE_ENV === "development") {
      console.log("[supabase/realtime] auth token synced")
    }
  } else if (process.env.NODE_ENV === "development") {
    console.warn("[supabase/realtime] no session token — Realtime may reject postgres_changes")
  }

  return accessToken ?? null
}

// ─── Formatting helpers ───────────────────────────────────────────────────────

export function buildErrorMessage(error: Error): string {
  if (error.message.includes("Invalid login credentials")) return "Email ou mot de passe incorrect"
  if (
    error.message.includes("User already registered") ||
    error.message.includes("A user with this email address has already been registered")
  ) {
    return "Un compte existe déjà avec cet email"
  }
  return "La soumission a échouée"
}

export function camelCasePayload<T>(payload: T | T[]): T | T[] {
  if (Array.isArray(payload)) {
    return payload.map((item) => camelCasePayload(item)) as T[]
  }
  if (payload !== null && typeof payload === "object") {
    return Object.fromEntries(
      Object.entries(payload as Record<string, unknown>).map(([k, v]) => [
        _toCamelCase(k),
        typeof v === "object" && v !== null && !Array.isArray(v)
          ? camelCasePayload(v)
          : Array.isArray(v)
            ? camelCasePayload(v)
            : v,
      ]),
    ) as T
  }
  return payload
}

export function parseTimestamp(value: string | Date | number | null | undefined): Date {
  if (value == null || value === "") return new Date(NaN)
  if (typeof value === "number") return new Date(value)
  if (value instanceof Date) return value

  let s = String(value).trim()
  if (!s) return new Date(NaN)

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T00:00:00.000Z`)

  if (s.includes(" ") && !s.includes("T")) s = s.replace(" ", "T")

  const hasExplicitOffset =
    /[zZ]$/.test(s) ||
    /[+-]\d{2}:\d{2}$/.test(s) ||
    /[+-]\d{4}$/.test(s) ||
    /[+-]\d{2}$/.test(s)

  if (!hasExplicitOffset && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return new Date(`${s}Z`)

  return new Date(s)
}

export function throwSupabaseError(
  error: PostgrestError | null,
  fallbackMessage = "Erreur lors du chargement",
): never {
  const err = new Error(error?.message || fallbackMessage) as Error & { status: number }
  err.status = error?.code ? Number(error.code) : 500
  throw err
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

type IlikeCapableQuery = {
  ilike: (column: string, pattern: string) => IlikeCapableQuery
  or: (filters: string) => IlikeCapableQuery
}

export function applyIlikeSearch<T extends IlikeCapableQuery>(
  query: T,
  search: Pick<Search, "key" | "value">,
  defaultColumn = "name",
): T {
  const searchTerm = search.value?.trim()
  if (!searchTerm) return query

  const { key } = search
  if (!key) return query.ilike(defaultColumn, `%${searchTerm}%`) as T
  if (typeof key === "string") return query.ilike(key, `%${searchTerm}%`) as T
  const conditions = key.map((col) => `${col}.ilike.%${searchTerm}%`).join(",")
  return query.or(conditions) as T
}

export async function deleteEntity(table: string, id: string): Promise<void> {
  const { error } = await getClient().from(table).delete().eq("id", id)
  if (error) throw error
}

export async function getEntities<T>(
  table: string,
  search: Search,
  select?: string | string[],
): Promise<T[]> {
  const { ascending, filter, ilikePrefix, limit, offset, order, value } = search
  const columns = select ? (typeof select === "string" ? select : select.join(",")) : "*"

  const isJoinEntry = (v: unknown): v is Record<string, string | number | boolean> =>
    typeof v === "object" && v !== null && !Array.isArray(v)

  const directFilter: Record<string, string | number | boolean> = {}
  const joinFilter: Record<string, Record<string, string | number | boolean>> = {}
  if (filter) {
    for (const [k, v] of Object.entries(filter)) {
      if (isJoinEntry(v)) joinFilter[k] = v
      else directFilter[k] = v as string | number | boolean
    }
  }

  let selectClause = columns
  if (Object.keys(joinFilter).length > 0) {
    const joinParts = Object.entries(joinFilter).map(([relation, eqCols]) => {
      const cols = Object.keys(eqCols).join(",")
      return `${relation}!inner(${cols})`
    })
    selectClause = `${columns},${joinParts.join(",")}`
  }

  let query = getClient().from(table).select(selectClause).range(offset, offset + limit - 1)

  if (order) query = query.order(order, { ascending: ascending ?? false })

  if (ilikePrefix?.column && ilikePrefix.prefix) {
    query = query.ilike(ilikePrefix.column, `${ilikePrefix.prefix}%`)
  }

  query = applyIlikeSearch(query, search)

  for (const [k, v] of Object.entries(directFilter)) {
    query = query.eq(k, v)
  }
  for (const [relation, eqCols] of Object.entries(joinFilter)) {
    for (const [col, v] of Object.entries(eqCols)) {
      query = query.eq(`${relation}.${col}`, v)
    }
  }

  const { data, error } = await query

  if (error) throwSupabaseError(error)

  let rows = (data ?? []) as unknown as Record<string, unknown>[]
  const relationKeys = Object.keys(joinFilter)
  if (relationKeys.length > 0) {
    rows = rows.map((row) => {
      const rest = { ...row }
      relationKeys.forEach((rel) => delete rest[rel])
      return rest
    })
  }
  return camelCasePayload(rows) as T[]
}

export async function getEntity<T>(
  table: string,
  value: string,
  select?: string | string[],
): Promise<T> {
  const columns = select ? (typeof select === "string" ? select : select.join(",")) : "*"

  const { data, error } = await getClient().from(table).select(columns).eq("id", value).single()

  if (error) throwSupabaseError(error)

  return camelCasePayload(data) as T
}

export async function patchEntity<T>(
  table: string,
  id: string,
  body: T,
  select?: string | string[],
): Promise<T | void> {
  const query = getClient().from(table).update(body as any).eq("id", id)

  if (select === undefined) {
    const { error } = await query
    if (error) throw error
    return undefined
  }

  const columns = typeof select === "string" ? select : select.join(",")
  const { data, error } = await query.select(columns).single()

  if (error) throw error

  return camelCasePayload(data) as T
}

export async function postEntity<T>(
  table: string,
  body: T,
  select?: string | string[],
): Promise<T | void> {
  const query = getClient().from(table).insert(body as any)

  if (select === undefined) {
    const { error } = await query
    if (error) throw error
    return undefined
  }

  const columns = typeof select === "string" ? select : select.join(",")
  const { data, error } = await query.select(columns).single()

  if (error) throw error

  return camelCasePayload(data) as T
}

export async function putEntity<T>(
  table: string,
  body: T,
  select?: string | string[],
): Promise<T | void> {
  const query = getClient().from(table).update(body as any)

  if (select === undefined) {
    const { error } = await query
    if (error) throw error
    return
  }

  const columns = typeof select === "string" ? select : select.join(",")
  const { data, error } = await query.select(columns).single()

  if (error) throw error

  return camelCasePayload(data) as T
}

// ─────────────────────────────────────────────────────────────────────────────
// # PRIVATE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function _toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}
